import type { FC } from "react"
import type { WithCapsuleCardFragment } from "~/contentful"
import Link from "~/components/Link"
import VerticalCard from "~/components/VerticalCard"
import type { Props as VerticalCardProps } from "~/components/VerticalCard"
import { ANALYTICS_EVENTS } from "~/helpers/analyticsEvents"

interface Props {
  item: WithCapsuleCardFragment
  origin: string
  sizeMobile: string
  sizeDesktop: string
  sizes?: string
  spacing?: boolean
  fullWidth?: boolean
  isMainContent?: boolean
  shouldEagerLoad?: boolean
}

const MultiImageCard: FC<Props> = ({
  item,
  origin,
  sizeMobile,
  sizeDesktop,
  sizes,
  spacing,
  fullWidth,
  isMainContent,
  shouldEagerLoad = false
}) => {
  // Server-side: use item.image (desktop image) as default since we don't know viewport
  // Client-side: CSS media queries will handle the responsive behavior
  const image = item?.image
  const imageMobile = item?.imageMobile
  const headerLevel =
    item.headerLevel === "h2" ||
    item.headerLevel === "h3" ||
    item.headerLevel === "h4"
      ? item.headerLevel
      : undefined
  let imageAboveHeading: VerticalCardProps["imageAboveHeading"] = undefined
  if (item.imageAboveHeading?.url) {
    imageAboveHeading = {
      src: item.imageAboveHeading.url,
      width: item.imageAboveHeading.width ?? undefined,
      height: item.imageAboveHeading.height ?? undefined
    }
  }

  return (
    <Link
      to={item?.url}
      openInNewWindow={item?.openInNewWindow ?? false}
      className="block h-full"
      trackClick={() => ({
        event: ANALYTICS_EVENTS.CARD_CLICKED,
        data: {
          path: window.location.pathname,
          url: item?.url,
          title: item?.altText,
          origin
        }
      })}
      aria-label={item?.altText ?? undefined}
    >
      <VerticalCard
        media={
          image?.url
            ? {
                ...image,
                type: "CONTENTFUL_IMAGE",
                url: image.url,
                srcMobile: imageMobile?.url ?? undefined,
                sizeMobile,
                sizeDesktop,
                sizes,
                altText: item?.altText ?? undefined
              }
            : null
        }
        rounded={spacing && !fullWidth}
        imageFillMode={item.imageFillMode as any}
        mediaPosition={item.imagePosition as any}
        imageMaxWidth={item.imageMaxWidth ?? undefined}
        imagePadding={item.imagePadding ?? undefined}
        imageAlignment={item.imageAlignment as any}
        badge={
          item.badgeText
            ? {
                text: item.badgeText,
                color: item.badgeColor ?? undefined
              }
            : undefined
        }
        header={item.header ?? undefined}
        headerLevel={headerLevel}
        body={item.body ?? undefined}
        tag={item.tag ?? undefined}
        imageAboveHeading={imageAboveHeading}
        textAlign={item.textAlignment as any}
        textColor={item.textColor ?? undefined}
        backgroundColor={item.backgroundColor ?? undefined}
        backgroundImage={item.backgroundImage?.url ?? undefined}
        button={
          item.buttonText
            ? {
                text: item.buttonText,
                openInNewWindow: item.openInNewWindow ?? false,
                color: item.buttonColor ?? undefined,
                style: item.buttonStyle as any,
                textColor: item.buttonTextColor ?? undefined
              }
            : undefined
        }
        countdownTargetDateTime={item.countdownTargetDateTime ?? undefined}
        isMainContent={isMainContent}
        shouldEagerLoad={shouldEagerLoad}
        overlayText={item.overlayText ?? false}
      />
    </Link>
  )
}

export default MultiImageCard
